/**
 * PhaseForm — the mobile-first full-screen sheet for one daily-routine phase.
 *
 * Fields, in order: traffic count (pre-filled from the auto feed when present;
 * editing it marks the source manual), transactions (closing phase only), the
 * phase checklist, a note, and photo capture. Single column, big touch targets,
 * sticky submit at the bottom.
 *
 * The report is an idempotent upsert on (store, date, phase). Photos attach to
 * an existing report id, so the flow is: submit the report first (which returns
 * its id), then the photo section unlocks. If the phase was already submitted,
 * the report id is known up front and photos can be added immediately.
 */

import { useEffect, useMemo, useState } from 'react';
import { X, Camera } from 'lucide-react';
import {
  useSubmitReport,
  PHASE_LABELS,
  type StorePhase,
  type StoreMe,
  type PhaseState,
  type ReportChecklistState,
} from '@/lib/useStores';
import { storeInputCls, storeLabelCls, storePrimaryBtnCls } from './storesShared';
import { PhotoCapture } from './PhotoCapture';

type TrafficAuto = { traffic: number | null; transactions: number | null } | null;

export function PhaseForm({
  phase,
  date,
  me,
  state,
  trafficAuto,
  onClose,
}: {
  phase: StorePhase;
  date: string;
  me: StoreMe;
  state: PhaseState | undefined;
  trafficAuto: TrafficAuto;
  onClose: () => void;
}) {
  const existing = state?.report;
  const checklistItems = useMemo(() => me.checklist[phase] ?? [], [me.checklist, phase]);
  const isClosing = phase === 'close';

  // Traffic: pre-fill from the existing report if submitted, else the auto feed.
  const autoTraffic = trafficAuto?.traffic ?? null;
  const initialTraffic =
    existing?.traffic_count != null
      ? String(existing.traffic_count)
      : autoTraffic != null
        ? String(autoTraffic)
        : '';

  const [traffic, setTraffic] = useState(initialTraffic);
  const [trafficEdited, setTrafficEdited] = useState(false);
  const [transactions, setTransactions] = useState(
    existing?.transactions != null ? String(existing.transactions) : '',
  );
  const [note, setNote] = useState(existing?.note ?? '');

  // Checklist state: start from the report's saved state, default unchecked.
  const initialChecks = useMemo(() => {
    const map: Record<number, boolean> = {};
    for (const item of checklistItems) map[item.id] = false;
    for (const c of existing?.checklist ?? []) map[c.item_id] = c.checked;
    return map;
  }, [checklistItems, existing]);
  const [checks, setChecks] = useState<Record<number, boolean>>(initialChecks);

  // The report id, known once the phase is submitted (now or previously).
  const [reportId, setReportId] = useState<number | null>(existing?.id ?? null);

  const submit = useSubmitReport();

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const trafficIsAutoUnedited = autoTraffic != null && !trafficEdited && existing?.traffic_count == null;

  const handleSubmit = () => {
    const checklist: ReportChecklistState[] = checklistItems.map((item) => ({
      item_id: item.id,
      checked: !!checks[item.id],
    }));
    const trafficNum = traffic.trim() === '' ? null : Number(traffic);
    const txNum = transactions.trim() === '' ? null : Number(transactions);
    submit.mutate(
      {
        date,
        phase,
        traffic_count: trafficNum,
        // Mark manual when the user edited the pre-filled value; auto otherwise.
        traffic_source: trafficIsAutoUnedited ? 'auto' : 'manual',
        transactions: isClosing ? txNum : undefined,
        note: note.trim() || undefined,
        checklist: checklist.length > 0 ? checklist : undefined,
        // An HQ caller acts on the picked store (the API accepts store_no from
        // stores:hq holders). Store users omit it and act on their own store.
        store_no: me.role === 'hq' ? me.store?.store_no : undefined,
      },
      {
        onSuccess: (data) => {
          setReportId(data.report.id);
        },
      },
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/40" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${PHASE_LABELS[phase]} check-in`}
        className="mt-auto flex max-h-[92vh] w-full flex-col rounded-t-2xl border-t border-amp-line bg-amp-white shadow-2 dark:border-amp-dark-line dark:bg-amp-dark-surface sm:mx-auto sm:my-auto sm:max-w-lg sm:rounded-2xl sm:border"
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-amp-line px-4 py-3 dark:border-amp-dark-line">
          <h2 className="text-[17px] font-semibold text-amp-ink dark:text-amp-dark-ink">
            {PHASE_LABELS[phase]} check-in
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-9 w-9 items-center justify-center rounded-full text-amp-ink-muted hover:bg-amp-cream-warm dark:text-amp-dark-ink-muted dark:hover:bg-amp-dark-surface-warm"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body (scrolls) */}
        <div className="min-h-0 flex-1 space-y-5 overflow-auto px-4 py-4">
          {/* Traffic count */}
          <div>
            <label htmlFor="traffic" className={storeLabelCls}>
              Traffic count (visitors)
            </label>
            <input
              id="traffic"
              type="number"
              inputMode="numeric"
              min={0}
              value={traffic}
              onChange={(e) => {
                setTraffic(e.target.value);
                setTrafficEdited(true);
              }}
              placeholder="0"
              className={storeInputCls}
            />
            {trafficIsAutoUnedited && (
              <p className="mt-1 text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
                Pre-filled from the store counter. Edit it if the count looks off.
              </p>
            )}
          </div>

          {/* Transactions (closing only) */}
          {isClosing && (
            <div>
              <label htmlFor="transactions" className={storeLabelCls}>
                Transactions
              </label>
              <input
                id="transactions"
                type="number"
                inputMode="numeric"
                min={0}
                value={transactions}
                onChange={(e) => setTransactions(e.target.value)}
                placeholder="0"
                className={storeInputCls}
              />
            </div>
          )}

          {/* Checklist */}
          {checklistItems.length > 0 && (
            <div>
              <p className={storeLabelCls}>{PHASE_LABELS[phase]} checklist</p>
              <div className="space-y-2">
                {checklistItems.map((item) => (
                  <label
                    key={item.id}
                    className="flex cursor-pointer items-center gap-3 rounded-lg border border-amp-line px-3 py-3 dark:border-amp-dark-line"
                  >
                    <input
                      type="checkbox"
                      checked={!!checks[item.id]}
                      onChange={(e) => setChecks((prev) => ({ ...prev, [item.id]: e.target.checked }))}
                      className="h-5 w-5 shrink-0 rounded border-amp-line accent-amp-olive"
                    />
                    <span className="text-[15px] text-amp-ink dark:text-amp-dark-ink">{item.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Note */}
          <div>
            <label htmlFor="note" className={storeLabelCls}>
              Note (optional)
            </label>
            {/* Stays a plain box. The Sweet Spot store app renders this note as
                plain text on the tablet, so HTML here would show raw tags to store
                staff, and that app is a separate repo. The column is also only
                VARCHAR(1000), and markup roughly doubles a note. */}
            <textarea
              id="note"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={
                isClosing
                  ? 'Anything HQ should know about today?'
                  : 'Anything blocking sales right now?'
              }
              className={storeInputCls}
            />
          </div>

          {/* Photos */}
          <div>
            <p className={storeLabelCls}>
              <span className="inline-flex items-center gap-1.5">
                <Camera className="h-4 w-4" aria-hidden /> Photos
              </span>
            </p>
            {reportId != null ? (
              <PhotoCapture reportId={reportId} existing={existing?.photos ?? []} />
            ) : (
              <p className="rounded-lg border border-dashed border-amp-line px-3 py-4 text-center text-[13px] text-amp-ink-muted dark:border-amp-dark-line dark:text-amp-dark-ink-muted">
                Submit this check-in first, then add a photo.
              </p>
            )}
          </div>

          {submit.error && (
            <p className="text-[13px] text-red-700 dark:text-red-300">
              Could not save: {submit.error.message}
            </p>
          )}
        </div>

        {/* Sticky submit */}
        <div className="shrink-0 border-t border-amp-line px-4 py-3 dark:border-amp-dark-line">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submit.isPending}
            className={storePrimaryBtnCls}
          >
            {submit.isPending
              ? 'Saving...'
              : reportId != null
                ? 'Save changes'
                : `Submit ${PHASE_LABELS[phase].toLowerCase()}`}
          </button>
          {reportId != null && !submit.isPending && (
            <p className="mt-2 text-center text-[12px] text-green-700 dark:text-green-300">
              Saved. You can add photos above or close this.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
